import React from 'react';
import classnames from 'classnames';
import { ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import { ChartTooltip as CustomTooltip } from './ChartTooltip';
import { ChartLegend as CustomLegend } from './ChartLegend';
import GlobalProps from '../../globalProps';

import './LineAreaChart.css';

const simpleRound = (num: number): number => {
  if (num > 100) {
    return Math.ceil(num / 100) * 100;
  }

  return Math.ceil(num / 10) * 10;
};

export type LineAreaChartValue = {
  name: string;
  line: number;
  area: number;
};

interface Props extends GlobalProps {
  dataset: LineAreaChartValue[];
  areaLabel: string;
  lineLabel: string;
}

export function LineAreaChart({ dataset, areaLabel, lineLabel, className, style }: Props): JSX.Element {
  const calculateLabelLength = (data: string[]): number =>
    data.reduce((highestVal, current) => (current.length > highestVal ? current.length : highestVal), 0);
  const lineWidth: number = calculateLabelLength(dataset.map((data) => data.line.toString())) * 15;
  const areaWidth: number = calculateLabelLength(dataset.map((data) => data.area.toString())) * 15;

  return (
    <div className={classnames('line-area-chart', className)} style={style}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={dataset}
          margin={{
            top: 5,
            right: 0,
            bottom: 60,
            left: 0,
          }}
        >
          <defs>
            <linearGradient id="colorarea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="50%" stopColor="#00EDFF"></stop>
              <stop offset="100%" stopColor="#58BBF3"></stop>
            </linearGradient>
          </defs>

          <CartesianGrid stroke="#E7E7E7" />

          <XAxis
            dataKey="name"
            type="category"
            tickSize={0}
            tickMargin={20}
            axisLine={{ stroke: '#E7E7E7' }}
            tick={{ fill: '#A8A8A8' }}
            tickFormatter={(value) => value.split('-')[0].trim()}
          />

          <YAxis
            yAxisId="left"
            tickSize={15}
            tickMargin={46}
            stroke="#EA3C23"
            tick={{ fill: '#A8A8A8' }}
            dataKey="line"
            tickFormatter={(a) => a || ''}
            width={Math.max(lineWidth, 60)}
            dx={35}
          />
          <YAxis
            yAxisId="right"
            tickSize={15}
            orientation="right"
            tickMargin={42}
            tick={{ fill: '#A8A8A8' }}
            dataKey="area"
            stroke="#00EDFF"
            tickFormatter={(a) => a || ''}
            dx={-35}
            domain={[0, (dataMax: number) => simpleRound(dataMax)]}
            width={Math.max(areaWidth, 60)}
          />

          <Line yAxisId="left" dot={false} dataKey="line" strokeWidth={2} stroke="#EA3C23" />
          <Area yAxisId="right" dataKey="area" fill="url(#colorarea)" stroke="#0DE2F2" />

          <Tooltip content={<CustomTooltip labels={[lineLabel, areaLabel]} />} />
          <Legend
            content={<CustomLegend labels={[lineLabel, areaLabel]} />}
            wrapperStyle={{ bottom: 0, color: '#4F4F4F' }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
